import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api, formatApiError, merkeSitzung, setzeSitzungsendeHandler } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, obj = user
  const [loading, setLoading] = useState(true);

  // Eine serverseitig beendete Sitzung (Passwortwechsel oder „auf allen Geräten
  // abmelden" auf einem anderen Gerät) meldet sich nur als 401 auf irgendeinem
  // Aufruf. Ohne diesen Draht bliebe die Oberfläche angemeldet aussehen und
  // jeder Klick liefe in eine Fehlermeldung.
  useEffect(() => {
    setzeSitzungsendeHandler(() => {
      merkeSitzung(false);
      setUser(false);
    });
    return () => setzeSitzungsendeHandler(null);
  }, []);

  const fetchMe = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      merkeSitzung(true);
      setUser(data);
    } catch (err) {
      // Not authenticated is expected on public pages — log for debugging only
      if (err?.response?.status && err.response.status !== 401) {
        console.warn("auth/me failed:", err.message);
      }
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = useCallback(async (email, password) => {
    // Backend sets httpOnly access_token cookie; we do not persist tokens client-side.
    const { data } = await api.post("/auth/login", { email, password });
    merkeSitzung(true);
    setUser(data);
    return data;
  }, []);

  const register = useCallback(async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    merkeSitzung(true);
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      console.warn("logout call failed (continuing to clear local state):", err.message);
    }
    merkeSitzung(false);
    setUser(false);
  }, []);

  // Auf allen Geräten abmelden. Anders als `logout` löscht das nicht nur die
  // Cookies dieses Browsers, sondern entwertet serverseitig JEDES bisher
  // ausgestellte Token des Kontos (token_version, siehe backend/auth_utils.py).
  // Fehler werden hier NICHT verschluckt: Wer „überall abmelden" drückt, muss
  // erfahren, wenn es nicht geklappt hat — sonst wiegt ihn eine grüne Meldung
  // in Sicherheit, während die fremde Sitzung weiterläuft.
  const logoutAll = useCallback(async () => {
    await api.post("/auth/logout-all");
    merkeSitzung(false);
    setUser(false);
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, register, logout, logoutAll, refresh: fetchMe, formatApiError }),
    [user, loading, login, register, logout, logoutAll, fetchMe],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
